import { useInfiniteReferrals } from "@/tanstack-hooks";
import Spinner from "./Spinner";
import EmptyState from "./EmptyState";
import { CloudOffIcon, LinkIcon } from "lucide-react";
import ReferralStepTracker from "./ReferralStepTracker";
import { UserGender } from "@/generated-api";
import { useTranslation } from "@/hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import SpinnerSmall from "./SpinnerSmall";

export default function ReferralsList({ border = true }: { border?: boolean }) {
  const t = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { ref, inView } = useInView({
    root: scrollContainerRef.current,
    rootMargin: "20px 0px", // Starts observing before the element comes into view
    threshold: 0,
  });
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteReferrals();
  const [isInitialDelay, setIsInitialDelay] = useState(true);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialDelay(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isInitialDelay) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      // Set a new timeout for fetching the next page
      fetchTimeoutRef.current = setTimeout(() => {
        void fetchNextPage();
      }, 250);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, isInitialDelay]);

  const referrals = data?.pages.flatMap((page) => page.items);

  return (
    <>
      <div className="font-semibold text-lg">
        {t.REFERRAL_LIST.LABELS.MY_REFERRALS}
      </div>
      <div
        className={`${
          border
            ? "border border-zinc-700 max-h-[50vh] p-4 sm:p-6"
            : "max-h-[80vh] px-4 pt-2"
        } rounded-2xl overflow-auto`}
      >
        {isLoading ? (
          <Spinner />
        ) : isError || !data || !referrals ? (
          <EmptyState
            icon={CloudOffIcon}
            title={t.REFERRAL_LIST.ERRORS.LOADING_TITLE}
            message={t.REFERRAL_LIST.ERRORS.LOADING_DESCRIPTION}
          />
        ) : referrals.length == 0 ? (
          <EmptyState
            icon={LinkIcon}
            title={t.REFERRAL_LIST.LABELS.NO_REFERRALS_TITLE}
            message={t.REFERRAL_LIST.LABELS.NO_REFERRALS_DESCRIPTION}
            small={true}
          />
        ) : (
          <div className="flex flex-col gap-y-6" ref={scrollContainerRef}>
            {referrals.map((referral, i) => (
              <div
                key={`${referral.display_name}-${i}`}
                className="flex flex-col gap-y-2"
              >
                <div className="text-sm">
                  {referral.display_name || "Unknown"}
                  {referral.gender &&
                    (referral.gender === UserGender.MALE ? ", M" : ", F")}
                </div>
                <ReferralStepTracker referral={referral} />
              </div>
            ))}
            {hasNextPage && (
              <div
                ref={ref}
                className="w-full h-8 flex items-center justify-center"
              >
                {isFetchingNextPage && <SpinnerSmall />}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
